if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        /**
         * 变体矩阵**空闲时预取**（May 2026-09-09：「SKU 切换不同时比较慢」）。
         * ⚠️ 放在 requestIdleCallback 里，不和首屏抢带宽 —— 首屏速度是第 2 优先级。
         *    实测这款商品的 /products/x.js 只有 12KB。
         */
        const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
        idle(() => this.loadVariantMatrix());
      }

      /**
       * 取一次 `/products/<handle>.js`，缓存成 { names, variants:[{options, available}] }。
       * 拿它在**发请求之前**就把选择修好 —— 见 localRepair。
       * ⚠️ 失败就返回 null，不抛：修不了就退回服务端那条路（慢一点，但仍然对）。
       */
      loadVariantMatrix() {
        if (this._matrixPromise) return this._matrixPromise;
        const base = (this.dataset.url || window.location.pathname).split('?')[0];
        this._matrixPromise = fetch(`${base}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            if (!j || !Array.isArray(j.variants)) return null;
            this._matrix = {
              names: (j.options || []).map((o) => (typeof o === 'string' ? o : o.name)),
              variants: j.variants.map((v) => ({ options: v.options, available: !!v.available })),
            };
            return this._matrix;
          })
          .catch(() => null);
        return this._matrixPromise;
      }

      /**
       * **发请求之前**就在本地把选择修成「买得到的那一组」。
       *
       * 为什么要有这一层：服务端那条修复路径要**两次**往返
       * （第一次拿回「买不了」，第二次才拿回修好的）。实测一次 775ms，
       * 两次就是 1.5 秒以上，手机网络下更久 —— 而这是买家选颜色尺码时每一下的代价。
       * 本地先算好，就只剩**一次**请求。
       *
       * 规矩和服务端那版一致：**她刚点的那一项钉死**，其余属性换成矩阵里第一条买得到的。
       * ⚠️ 矩阵没就位、属性名对不上、找不到候选 —— 一律返回 null 退回服务端那条路。
       *    这一层是**加速**，不是唯一保障；宁可慢一点也不能挑错组合。
       */
      localRepair(target) {
        const m = this._matrix;
        const vs = this.variantSelectors || this.querySelector('variant-selects');
        if (!m || !vs) return null;

        const groups = new Map();
        vs.querySelectorAll('fieldset input, select option').forEach((el) => {
          const name = el.dataset.optionName;
          if (!name) return;
          if (!groups.has(name)) groups.set(name, []);
          groups.get(name).push({
            value: el.value != null && el.value !== '' ? el.value : (el.textContent || '').trim(),
            id: el.dataset.optionValueId,
            on: el.checked || el.selected,
          });
        });
        const order = m.names.filter((n) => groups.has(n));
        if (!order.length || order.length !== groups.size) return null;   // 名字对不上就别猜

        const current = order.map((n) => groups.get(n).find((x) => x.on)?.value ?? null);
        if (current.some((x) => x == null)) return null;

        // 当前这一组本来就买得到 → 不用修，走原来的值
        const hit = m.variants.find((v) => order.every((_, i) => v.options[i] === current[i]));
        if (hit && hit.available) return null;

        const ti = order.indexOf(target?.dataset?.optionName);
        const cand = m.variants.find((v) => v.available && (ti < 0 || v.options[ti] === current[ti]));
        if (!cand) return null;                                          // 钉死那一项之后一条都买不到

        const ids = order.map((n, i) => groups.get(n).find((x) => x.value === cand.options[i])?.id);
        if (ids.some((x) => !x)) return null;
        return ids;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        /**
         * ⚠️⚠️ **记下她刚点的是哪一项。**
         * 下面 `handleUpdateProductInfo` 里的「组合不存在就自动挑一组有货的」要用它，
         * 而那个回调 `(html) => {...}` 的作用域里**根本没有 target**（只有 targetId，
         * 还在另一个函数里）。2026-08-26 那版直接写了 `target`，
         * 结果每次组合不存在都抛 `ReferenceError: target is not defined` ——
         * 异常被 fetch 的 .catch 吞掉，页面表现成「点了完全没反应」，
         * 整个自动跳转从写下那天起**一次都没生效过**（2026-08-27 抓控制台才发现）。
         */
        this._touchedTarget = target;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        /**
         * 本地先把选择修成买得到的那一组 —— 修得动就只发**一次**请求，
         * 修不动（矩阵还没到、名字对不上）就照旧发原来的值，由服务端那条路兜底。
         * ⚠️ 换商品（shouldSwapProduct）时不修：那是另一个商品，矩阵不是这一份。
         */
        const localFixed = shouldSwapProduct ? null : this.localRepair(target);
        if (localFixed) this._repairing = true;   // 已经修过一轮，别让服务端那条再修一次

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, localFixed ?? selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          /**
           * May 2026-08-26:「选择不同的 model 商品时，没有自动切换商品页，
           * 因为属性2和3有些 model 是没有的，没有自动跳转到有库存的可以选购的商品」
           *
           * Dawn 原来到这一步就 setUnavailable() —— 页面写个 Unavailable 就不动了,
           * 买家自己去猜哪个颜色配哪个尺码才有货。批发买家不会猜,他会走。
           *
           * 现在:**保住她刚点的那一项**,后面的属性各自换成第一个还有货的值,再请求一次。
           *
           * ⚠️⚠️ **判据是「买不了」不是「组合不存在」**（May 2026-09-09 复现出来的）。
           *    原来写的是 `if (!variant)` —— 只覆盖了「这个组合压根没有这条变体」。
           *    可实际最常见的是**组合存在、但那条变体没货**：
           *    实测这款耳钉，选中 Green 时点 Pink，结果是
           *      MODEL = Pink ✓ ／ COLOR = Gold/Green ✓(无货)，而 Gold/Pink 有货却没被选中。
           *    variant 不是 null（那条变体确实存在），所以修复逻辑一次都没进来 ——
           *    买家看到的就是「选了个买不了的东西」，正是她说的
           *    「没有随即跳转为可选购的」。
           *
           * ⚠️ 只修一次(`_repairing` 门闩)。修完还是不能买就老老实实认了 ——
           *    不设门闩的话,遇到整款都无货会来回请求打死自己。
           */
          const cannotBuy = !variant || variant.available === false;
          if (cannotBuy) {
            // ⚠️ 用 `this._touchedTarget`，**不能写 `target`** —— 这个箭头函数的作用域里没有它。
            //    见 handleOptionValueChange 里那段注释：写成 target 会抛 ReferenceError，
            //    而异常被 fetch 的 .catch 吞掉，表现成「点了没反应」，非常难查。
            const touchedTarget = this._touchedTarget;
            const repaired = !this._repairing && this.repairSelection(html, touchedTarget);
            if (repaired) {
              this._repairing = true;
              this.renderProductInfo({
                requestUrl: this.buildRequestUrlWithParams(productUrl, repaired),
                targetId: touchedTarget?.id,
                callback: this.handleUpdateProductInfo(productUrl),
              });
              return;
            }
          }
          this._repairing = false;

          /**
           * ⚠️ 修不动的时候要分两种情况，**不能一律 setUnavailable()**：
           *   · 组合根本不存在  → setUnavailable()（价格、SKU 那些本来就没有）
           *   · 组合存在但没货  → **照常渲染**，Dawn 会显示售罄 + 正确的价格和图片。
           *     一律 setUnavailable 的话，一个只是暂时缺货的款会连价格和图都被抹掉。
           */
          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          // 描述区那两格也要跟着变体走(二.9)——不接的话换了变体，
          // Details 里还写着上一个变体的货号和克重，比不显示更糟
          updateSourceFromDestination('TorenaSku');
          updateSourceFromDestination('TorenaWeight');
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      /**
       * 组合不存在时，挑一组真的能买的出来。
       *
       * 规矩:**她刚点的那个属性不动**(那是她的意图),其余属性
       * —— 当前值还有货就留着,没货就换成这一组里第一个有货的。
       * 返回 option value id 数组(和 `selectedOptionValues` 一个格式);
       * 挑不出完整的一组就返回 null,交给 setUnavailable()。
       *
       * @param {Document} html 服务端刚返回的那一版(availability 是按当前选择算的)
       * @param {Element}  target 她刚点的那个 input / select
       */
      repairSelection(html, target) {
        const selects = html.querySelector('variant-selects');
        if (!selects) return null;

        const touched = target.dataset?.optionName ?? this.getInputForEventTarget?.(target)?.dataset?.optionName;
        const groups = new Map();                       // 属性名 → 这一组的所有可选值
        selects.querySelectorAll('select option, fieldset input').forEach((el) => {
          const name = el.dataset.optionName;
          if (!name) return;
          if (!groups.has(name)) groups.set(name, []);
          groups.get(name).push(el);
        });
        if (!groups.size) return null;

        const picked = [];
        let changed = false;
        for (const [name, els] of groups) {
          const current = els.find((el) => el.selected || el.checked) ?? els[0];
          // 她刚点的那一项原样保留,哪怕服务端说它没货 —— 换掉它等于无视她的操作
          if (name === touched) { picked.push(current?.dataset.optionValueId); continue; }
          if (current?.dataset.available === 'true') { picked.push(current.dataset.optionValueId); continue; }
          const ok = els.find((el) => el.dataset.available === 'true');
          if (!ok) return null;                          // 这一组一个有货的都没有 → 修不了
          picked.push(ok.dataset.optionValueId);
          changed = true;
        }
        // 一个都没换说明不是「组合不存在」造成的,再请求一次只是白跑
        if (!changed || picked.some((x) => !x)) return null;
        return picked;
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
